/**
 * The two sides `wayai knowledge migrate` talks to, over the CLI's one login: WayAI's setup and
 * file routes for the knowledge it reads, and the `/api/data` proxy for the knowledge base it
 * writes.
 *
 * Every request to a route with a per-person budget is paced below it — the budget the person's
 * other commands and web console share — and waits out a 429 when that budget is spent: the data
 * surface at the rate `wayai files push` keeps, the setup routes and `POST /api/files/sign` each at
 * four fifths of theirs. A signed URL's bytes are fetched without the session, from no budgeted route.
 */

import { getEndpointConfig } from '@wayai/core';
import { DATA_PROXY_PREFIX } from '@wayai/core/contracts';
import { ApiError, type ApiClient } from '../lib/api-client.js';
import { expected } from '../lib/expected.js';
import { Pacer, type PacerOptions } from '../communities/pacer.js';
import type { DataClient } from '../data/client.js';
import { getBaseRecord } from '../data/config-as-code/api.js';
import { ENTITY_KINDS } from '../data/config-as-code/types.js';
import { fileWriteHeaders } from '../data/files-sync/headers.js';
import { pathSegment, pathSegments } from '../data/helpers.js';
import type { BaseRow, HubListing, KnowledgeBase, KnowledgeReader, RemoteFile, RemoteFileType } from './migrate.js';
import type { DesiredFile, FileTypeSpec } from './plan.js';

/** Rows per file listing request: the most the listing returns. */
const LIST_PAGE = 1000;
/** Listing pages read before refusing: a million files, or a server answering in circles. */
const MAX_LIST_PAGES = 1000;

/** The label every version this command writes carries, so a file's history says where it came from. */
export const MIGRATION_LABEL = 'wayai knowledge migrate';

/** Four fifths of the per-person budget of the routes `path` falls under, as `wayai files push` paces its own. */
export function pacedRequestsPerMinute(path: string, fallback: number): number {
  const budget = getEndpointConfig(path)?.userLimit;
  return budget ? Math.max(1, Math.floor((budget * 4) / 5)) : fallback;
}

/** A pacer for one budget, saying why it waits. */
function pacerFor(path: string, what: string): Pacer {
  const perMinute = pacedRequestsPerMinute(path, 60);
  const onWait: PacerOptions['onWait'] = (ms, reason) =>
    console.error(
      reason === 'paced'
        ? `  Pacing ${what} requests at ${perMinute} a minute: waiting ${Math.ceil(ms / 1000)} s.`
        : `  The ${what} request budget is spent (your other commands and the web console share it): waiting ${Math.ceil(ms / 1000)} s.`,
    );
  return new Pacer({ perMinute, onWait });
}

/** The hubs the caller can see in `orgId`, production and previews, from the setup schema. */
export function hubsOf(schema: Awaited<ReturnType<ApiClient['getUserSchema']>>, orgId: string): HubListing[] {
  return (schema.data?.flat?.hubsByOrg?.[orgId] ?? []).map((hub) => ({
    hub_id: hub.id,
    hub_name: hub.name,
    hub_environment: typeof hub.hub_environment === 'string' ? hub.hub_environment : '',
    production_hub_id: typeof hub.production_hub_id === 'string' && hub.production_hub_id ? hub.production_hub_id : null,
  }));
}

/** The routes whose per-person budgets the reader paces itself below: one pacer each. */
export const READER_BUDGETS = { setup: '/api/setup/organization-resources', sign: '/api/files/sign' } as const;

/** The reader's pacers: the setup routes', and `POST /api/files/sign`'s, which is a budget of its own. */
export interface ReaderPacers {
  setup: Pick<Pacer, 'run'>;
  sign: Pick<Pacer, 'run'>;
}

/** WayAI's knowledge resources, read through the setup routes. */
export function knowledgeReader(
  api: ApiClient,
  orgId: string,
  pacers: ReaderPacers = { setup: pacerFor(READER_BUDGETS.setup, 'setup'), sign: pacerFor(READER_BUDGETS.sign, 'file signing') },
): KnowledgeReader {
  const { setup, sign } = pacers;
  const run = <T>(send: () => Promise<T>) => setup.run(send);
  return {
    listOrgResources: async () => (await run(() => api.listOrgResources(orgId))).data ?? [],
    listOrgResourceFiles: async (id) => (await run(() => api.listOrgResourceFiles(orgId, id))).data ?? [],
    listOrgResourceFolders: async (id) => (await run(() => api.listOrgResourceFolders(orgId, id))).data ?? [],
    listHubs: async () => hubsOf(await run(() => api.getUserSchema()), orgId),
    listHubResources: async (hubId) => (await run(() => api.listHubResources(hubId))).data ?? [],
    listHubResourceFiles: async (hubId, id) => (await run(() => api.listHubResourceFiles(hubId, id))).data ?? [],
    listHubResourceFolders: async (hubId, id) => (await run(() => api.listHubResourceFolders(hubId, id))).data ?? [],
    readContent: async (ref) => {
      const url =
        ref.kind === 'org'
          ? (await run(() => api.getOrgResourceFileContent(orgId, ref.orgFileId))).data.signed_url
          : (await sign.run(() => api.signFileUrl(ref.bucket, ref.objectKey))).signedUrl;
      return api.downloadSigned(url);
    },
  };
}

/** `read`'s answer, or null when the base answers 404. */
async function unlessMissing<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** The knowledge base, through the data proxy. */
export function knowledgeBase(client: DataClient): KnowledgeBase {
  const pacer = pacerFor(`${DATA_PROXY_PREFIX}/files`, 'data');
  const request = <T>(method: string, path: string, body?: unknown) => pacer.run(() => client.request<T>(method, path, body));
  const baseSeg = (id: string) => pathSegment(id, '--base');
  const typeSeg = (id: string) => pathSegment(id, 'file type');

  return {
    getBase: (id) => unlessMissing(() => pacer.run(() => getBaseRecord(client, id) as Promise<BaseRow>)),
    listPreviews: (id) =>
      pacer.run(() =>
        client.collectPages<BaseRow>((cursor) => `/v1/${baseSeg(id)}/previews${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
      ),
    listFileTypes: async (base) => (await request<RemoteFileType[]>('GET', `/v1/${baseSeg(base)}/file-types`)) ?? [],
    getFileType: (base, id) => unlessMissing(() => request<RemoteFileType>('GET', `/v1/${baseSeg(base)}/file-types/${typeSeg(id)}`)),
    async putFileType(base, spec: FileTypeSpec) {
      const { id, ...body } = spec;
      await request('PUT', `/v1/${baseSeg(base)}/file-types/${typeSeg(id)}`, body);
    },
    async promoteFileTypes(production, preview, ids, dryRun) {
      // Every other config section is named EMPTY: a section left out is promoted whole, which
      // would ship whatever else the preview holds. (Credentials are not a section: like every
      // promotion, this one publishes the ones the preview opted in to production.)
      return request('POST', `/v1/${baseSeg(production)}/promote`, {
        source_base_id: preview,
        dry_run: dryRun,
        ...Object.fromEntries(ENTITY_KINDS.map((kind) => [kind, []])),
        file_types: ids,
      });
    },
    async listFiles(base, fileType) {
      const rows: RemoteFile[] = [];
      for (let page = 0; page < MAX_LIST_PAGES; page++) {
        const qs = new URLSearchParams({ limit: String(LIST_PAGE), offset: String(page * LIST_PAGE) });
        const pageRows = (await request<RemoteFile[]>('GET', `/v1/${baseSeg(base)}/files/${typeSeg(fileType)}?${qs}`)) ?? [];
        for (const row of pageRows) rows.push(row);
        if (pageRows.length < LIST_PAGE) return rows;
      }
      throw expected(`The listing of file type "${fileType}" on base "${base}" did not end after ${MAX_LIST_PAGES} pages.`);
    },
    async putFile(base, file: DesiredFile, bytes: Uint8Array) {
      await pacer.run(() =>
        client.upload(
          `/v1/${baseSeg(base)}/files/${typeSeg(file.fileType)}/${pathSegments(file.path)}`,
          bytes,
          file.contentType,
          fileWriteHeaders({ label: MIGRATION_LABEL, metadata: file.metadata }),
        ),
      );
    },
  };
}
